import React from 'react';
import { Provider } from 'react-redux';
import NodeCache from 'node-cache';

// global store cache to allow for thread safe usages of redux-eggs
const storeCache = new NodeCache({
  stdTTL: 120
});

// TODO: Does this need to be enhanced to address undefined values within arrays?
function flushObject(obj) {
  // eslint-disable-next-line no-restricted-syntax
  for (const prop in obj) {
    if (typeof obj[prop] === 'object') {
      flushObject(obj[prop]);
    } else if (typeof obj[prop] === 'undefined') {
      delete obj[prop];
    }
  }
  return obj;
}
function createWrapperInitializer(storeCreator) {
  let appEggsLocal = [];
  let appHasGetInitialProps = false;
  let beforeResult;
  const createStore = ({
    eggs = [],
    requestId,
    key
  }) => {
    const store = storeCreator();
    store.addEggs(eggs);
    storeCache.set(requestId, store);
    return store;
  };
  const getStore = ({
    requestId,
    clearCache
  }) => {
    const store = storeCache.get(requestId);
    if (clearCache) {
      storeCache.del(requestId);
    }
    return store;
  };
  const withAppEggs = (pageEggs = []) => [].concat(appEggsLocal, pageEggs);
  const getLocalFn = (fnOrProps = {}, isGetInitialProps = false) => {
    if (typeof fnOrProps === 'function') {
      return fnOrProps;
    }
    const props = {
      ...fnOrProps
    };
    return () => () => isGetInitialProps ? props : {
      props
    };
  };
  const runBeforeResult = async (store, context) => {
    if (beforeResult) {
      await beforeResult(store, {
        context
      });
    }
  };
  return {
    getAppWrapper(appEggs = [], options = {}) {
      appEggsLocal = appEggs;
      beforeResult = options.beforeResult;
      return {
        wrapGetInitialProps(fn) {
          appHasGetInitialProps = true;
          return async context => {
            const page = context.Component;
            const requestId = context.ctx.req.id;
            const store = createStore({
              key: 'app:wrapGetInitialProps',
              eggs: withAppEggs(page.__eggs),
              requestId
            });
            const appProps = await fn(store)(context);
            if (!page.__eggsConfig?.gspOrGsspDetected) {
              await runBeforeResult(store, context);
            }
            appProps.__BONNET_REQ_ID = requestId;
            appProps.pageProps = {
              ...appProps.pageProps,
              __eggsState: store.getState()
            };
            return appProps;
          };
        },
        wrapApp(AppComponent) {
          return class WrappedApp extends React.Component {
            static getInitialProps = AppComponent.getInitialProps;
            render() {
              const requestId = this.props.__BONNET_REQ_ID;
              const key = 'AppWrapper';
              const store = getStore({
                requestId,
                clearCache: true
              }) || createStore({
                requestId,
                key
              });
              return /*#__PURE__*/React.createElement(Provider, {
                store
              }, /*#__PURE__*/React.createElement(AppComponent, this.props));
            }
          };
        }
      };
    },
    getPageWrapper(pageEggs = []) {
      const eggsConfigValue = {};
      const wrap = fnOrProps => {
        eggsConfigValue.gspOrGsspDetected = true;
        const localFn = getLocalFn(fnOrProps);
        return async context => {
          const requestId = context.req.id;
          const key = 'wrap';
          let store = getStore({
            requestId});
          if (!appHasGetInitialProps) {
            store = createStore({
              key,
              eggs: withAppEggs(pageEggs),
              requestId
            });
          }
          const result = await localFn(store)(context);
          await runBeforeResult(store, context);
          if ('props' in result) {
            return {
              ...result,
              props: {
                ...result.props,
                __eggsState: flushObject(store.getState())
              }
            };
          }
          return result;
        };
      };
      return {
        wrapGetInitialProps(fnOrProps) {
          const localFn = getLocalFn(fnOrProps, true);
          return async context => {
            const requestId = context.req.id;
            const key = 'page:wrapGetInitialProps';
            let store = getStore({
              requestId});
            if (!appHasGetInitialProps) {
              store = createStore({
                key,
                eggs: withAppEggs(pageEggs),
                requestId
              });
            }
            const pageProps = await localFn(store)(context);
            if (!appHasGetInitialProps) {
              await runBeforeResult(store, context);
            }
            return {
              ...pageProps,
              __eggsState: store.getState()
            };
          };
        },
        wrapGetStaticPaths(fn) {
          return async context => {
            const store = storeCreator();
            const result = await fn(store)(context);
            await runBeforeResult(store, context);
            return result;
          };
        },
        wrapGetStaticProps: wrap,
        wrapGetServerSideProps: wrap,
        // TODO: Add error for unwrapped GSP or GSSP pages
        wrapPage(page) {
          const eggsConfig = {
            __eggs: pageEggs,
            __eggsConfig: eggsConfigValue
          };
          return Object.assign(page, eggsConfig);
        }
      };
    }
  };
}

export { createWrapperInitializer };
